import type { Address, Party } from '@my/directory-api';
import {
  findVariantPreset,
  type CargoDefinition,
  type Order,
  type OrderTask,
  type OrderTaskCargoItem,
  type TaskStateValue,
} from '@my/tms-api';
import { CARGO_HANDLING_TASK_TYPES, cargoHandlingPhaseFor } from '../../orders/lib/cargoPick';

/** Cargo types surfaced in the manifest queue for now — Pallet + (Full/Part) Load. */
const QUEUE_CARGO_TYPES = new Set<string>(['Pallet', 'FullLoad', 'PartLoad']);

/** One collection/delivery cargo line in the manifesting queue. */
export interface QueueRow {
  /** Cargo item id — the unit of load assignment (the "load number" lives here). */
  itemId: string;
  orderId: string;
  orderNumber: string;
  /** Which leg this line belongs to. */
  role: 'collection' | 'delivery';
  /** This leg's task state (Completed = collected/delivered, Cancelled = dead). */
  taskState: TaskStateValue;
  /** Whether the order's collection leg is already completed (goods picked up). */
  collectionCompleted: boolean;
  quantity: number | null;
  unit: string | null;
  /** Cargo line description / handling-unit type (e.g. "Euro Pallet", "Combi"). */
  itemName: string;
  /** Loading metres this line occupies, when derivable from the cargo variant. */
  lm: number | null;
  customerName: string;
  accountCode: string;
  /** Planned time-window label for this leg ("AM", "PM", "08:00", "Any"). */
  window: string;
  collectionAddress: string;
  /** Site/location name (the order-task-header title) for the collection point. */
  collectionName: string;
  collectionCity: string;
  collectionCountry: string;
  deliveryAddress: string;
  /** Site/location name for the delivery point. */
  deliveryName: string;
  deliveryCity: string;
  deliveryCountry: string;
  /** Planned date + window per leg, e.g. "02/12/24 PM". */
  collectionWhen: string;
  deliveryWhen: string;
  /** Temperature set point (°C) for this leg — task-level, then order-level. */
  setPointC: number | null;
  /** Map point for this leg (collection coords for collect, delivery for deliver). */
  lat: number | null;
  lng: number | null;
  /** Address id of this leg's point — used to persist the load's furthest point. */
  addressId: string | null;
}

function windowLabel(task: OrderTask): string {
  if (task.timeMode === 'AM') return 'AM';
  if (task.timeMode === 'PM') return 'PM';
  if (task.timeMode === 'Exact' && task.plannedTime) return task.plannedTime.slice(0, 5);
  return 'Any';
}

/** "2026-06-05" → "05/06/26". */
function fmtDmy(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y.slice(2)}` : iso;
}

/** Planned date + window for a leg, e.g. "02/12/24 PM" (or "—" when unplanned). */
function whenLabel(task: OrderTask | undefined): string {
  if (!task?.plannedDate) return '—';
  return `${fmtDmy(task.plannedDate)} ${windowLabel(task)}`;
}

/**
 * Loading metres for a cargo line. Prefer the persisted value (now saved on
 * the item for all cargo types); fall back to deriving from the matched pallet
 * variant for legacy rows saved before the column existed.
 */
function lmForItem(
  cargoDef: CargoDefinition | undefined,
  item: OrderTaskCargoItem,
): number | null {
  if (item.loadingMetresM != null) return item.loadingMetresM;
  if (item.quantity == null || !cargoDef) return null;
  const variant = cargoDef.variants.find((v) => v.name === item.description);
  if (!variant) return null;
  const per = variant.presetKey
    ? (findVariantPreset(cargoDef.cargoType, variant.presetKey)?.loadingMetres ??
      variant.loadingMetres)
    : variant.loadingMetres;
  return per != null ? per * item.quantity : null;
}

/**
 * Flatten the open orders into one queue row per **cargo item** on a
 * collection/delivery task within the horizon. Each order contributes its
 * collection + delivery cities to every row so the route reads at a glance.
 */
export function buildQueueRows(args: {
  orders: Order[];
  tasksByOrderId: Map<string, OrderTask[]>;
  customersById: Map<string, Party>;
  addressById: Map<string, Address>;
  cargoDefsById: Map<string, CargoDefinition>;
  /** Horizon end (yyyy-MM-dd) — rows are included up to and including this day. */
  endYmd: string;
}): QueueRow[] {
  const { orders, tasksByOrderId, customersById, addressById, cargoDefsById, endYmd } = args;
  const addrParts = (addressId: string | undefined) => {
    const a = addressId ? addressById.get(addressId) : undefined;
    return {
      address: a?.fullAddress || a?.name || '—',
      name: a?.name || '—',
      city: a?.city || '—',
      country: a?.countryIsoCode || '—',
      lat: a?.latitude ?? null,
      lng: a?.longitude ?? null,
    };
  };

  const rows: QueueRow[] = [];
  for (const order of orders) {
    const cargoDef = cargoDefsById.get(order.cargoDefinitionId);
    // Restrict the queue to Pallet + Load cargo types for now.
    if (!cargoDef || !QUEUE_CARGO_TYPES.has(cargoDef.cargoType)) continue;
    const customer = customersById.get(order.customerPartyId);
    const tasks = (tasksByOrderId.get(order.id) ?? [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence);

    const handlingTasks = tasks.filter((t) => CARGO_HANDLING_TASK_TYPES.has(t.type));
    const collTask = handlingTasks.find((t) => cargoHandlingPhaseFor(t.type) === 'load');
    const delTask = handlingTasks.find((t) => cargoHandlingPhaseFor(t.type) === 'delivery');
    const coll = addrParts(collTask?.addressId);
    const del = addrParts(delTask?.addressId);
    const collectionCompleted = collTask?.state === 'Completed';
    const collectionWhen = whenLabel(collTask);
    const deliveryWhen = whenLabel(delTask);

    for (const task of handlingTasks) {
      const items = task.cargoItems ?? [];
      if (items.length === 0) continue;
      // Everything up to and including the horizon's end day (overdue items
      // included so they still get planned); whole-day, since we compare dates
      // not times. Items with no planned date are excluded.
      const pd = task.plannedDate ?? '';
      if (!pd || pd > endYmd) continue;
      const role: QueueRow['role'] =
        cargoHandlingPhaseFor(task.type) === 'delivery' ? 'delivery' : 'collection';

      for (const item of items) {
        rows.push({
          itemId: item.id,
          orderId: order.id,
          orderNumber: order.orderNumber,
          role,
          taskState: task.state,
          collectionCompleted,
          quantity: item.quantity ?? null,
          unit: item.quantityUnit ?? null,
          itemName: item.description?.trim() || '—',
          lm: lmForItem(cargoDef, item),
          customerName: customer?.name ?? '—',
          accountCode: customer?.code ?? '—',
          window: windowLabel(task),
          collectionAddress: coll.address,
          collectionName: coll.name,
          collectionCity: coll.city,
          collectionCountry: coll.country,
          deliveryAddress: del.address,
          deliveryName: del.name,
          deliveryCity: del.city,
          deliveryCountry: del.country,
          collectionWhen,
          deliveryWhen,
          setPointC: task.temperatureC ?? order.temperatureC ?? null,
          lat: role === 'collection' ? coll.lat : del.lat,
          lng: role === 'collection' ? coll.lng : del.lng,
          addressId: (role === 'collection' ? collTask?.addressId : delTask?.addressId) ?? null,
        });
      }
    }
  }
  return rows;
}
